<template>
	<div v-if="chartData">
		<div v-if="showTotal" class="Well">
			<div class="Well__header">{{ totalLabel }}</div>
			<div class="Well__content">
				<span
					v-if="showValueComparison"
					class="pull-left ext-padding-large-right"
					>{{ comparisonValuesSum }}
					<!-- @vue-expect-error -->
					{{ chartData.datasets[0].value_unit }}</span
				>
				<span :class="showValueComparison ? 'dark-pastel-3-underline' : ''"
					>{{ valuesSum }}
					<!-- @vue-expect-error -->
					{{ chartData.datasets[0].value_unit }}</span
				>
			</div>
			<!-- This lives inside the `.Well`so that it can be placed relative to the well's height -->
			<div
				v-if="showValueComparison"
				class="button button--secondary OverlayContainer"
				style="position: absolute; top: calc(100% + 8px); left: 0; padding: 4px;"
				v-on:click="toggleValueColumn"
			>
				<span
					:class='`senaryon-icon senaryon-icon-outcomes senaryon-icon--larger-text${toggleButtonActive ? " dark-pastel-3-text" : ""}`'
				></span>
				<span class="OverlayContainer__content">{{
					toggleButtonOverlayText()
				}}</span>
			</div>
		</div>
		<donut-chart
			:chart-data="chartData"
			:chart-options="chartOptions"
		></donut-chart>
		<div v-if="showLabels" class="DonutLabels">
			<div
				:class="`DonutLabel ${useGenericColors ? `dark-pastel-${index + 1}` : ap.property.color} clearfix`"
				v-for="(ap, index) in pieDataProps"
				:key="ap.id"
			>
				<div class="pull-left property-color-box ext-margin-base-right"></div>
				<span class="pull-left">{{ getApValue(ap, apValueColumn) }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import DonutChart from "../global_components/graphs_and_visualizations/donut_chart.vue";
// Other components
import { i18n } from "../utils";

export default {
	mixins: [i18n],
	props: {
		showLabels: Boolean,
		showTotal: Boolean,
		showValueComparisonWith: {
			type: String,
			default: null,
		},
		forActorIds: Object,
		forPhaseId: Number,
		forPropTypes: Array,
		srvApValueColumn: String,
		addPropertyOwnerAsLabel: Boolean,
		srvPieColorClasses: Array,
		useGenericColors: Boolean, // Will be ignored if `srvPieColorClasses` is provided
		showDonutChartLabels: Boolean,
		chartCutoutPercentage: {
			type: Number,
			default: 82,
		},
		showDataShare: Boolean,
	},
	components: {
		"donut-chart": DonutChart,
	},
	data() {
		return {
			apValueColumn: this.srvApValueColumn,
			toggleButtonActive: this.srvApValueColumn == "current_value",
		};
	},
	computed: {
		pieDataProps() {
			return VueStore.getters.actorsPropertiesOfTypeFor(
				this.forActorIds,
				this.forPropTypes,
			);
		},
		pieLabels() {
			const pieLabels = this.pieDataProps.map((ap) => {
				let label = ap.property.names[I18n.locale];
				if (this.addPropertyOwnerAsLabel) {
					return `${label} (${VueStore.getters.actorData(ap.actor_id).names[I18n.locale].replace("Ausschuss ", "")})`; // Hack to get only the resp. ministry's name in the BMF Game
				}
				return label; // Elese
			});
			return pieLabels;
		},
		pieColors() {
			if (this.srvPieColorClasses) {
				// @ts-expect-error
				return this.srvPieColorClasses.map((colorClass) =>
					pp.viewHelpers.detectCssRuleFor(
						`.${colorClass}-bg`,
						"background-color",
					),
				);
			}
			// Else
			return this.pieDataProps.map((ap, index) => {
				return pp.viewHelpers.detectCssRuleFor(
					this.useGenericColors
						? `.dark-pastel-${index + 1}-bg`
						: `.${ap.property.color}-bg`,
					"background-color",
				);
			});
		},
		pieData() {
			let collectedData = this.pieDataProps.map((ap) => {
				const value = this.getApValue(ap, this.apValueColumn);
				return value < 0 ? 0 : value;
			});
			if (!this.showDataShare) {
				return collectedData;
			}
			const dataSum = collectedData.reduceRight((acc, cur) => acc + cur, 0);
			return collectedData.map((value) => {
				// Round to 2 decimal places (can't use `toFixed` because it convertes the nubmer into a string, which the ´donut-chart` can't parse
				return Math.round((value / dataSum) * 10000) / 100;
			});
		},
		chartData() {
			const valueUnitsofProps = Array.from(this.pieDataProps, (ap) => {
				const valueUnits = ap.property.value_units;
				if (valueUnits !== undefined) {
					let valueUnit =
						valueUnits[I18n.locale] || Object.values(valueUnits)[0] || "";
					if (valueUnit.length) {
						return valueUnit;
					}
				}
				return null;
			}).filter((el) => el); // Filter out empty entries
			return {
				labels: this.pieLabels,
				datasets: [
					{
						backgroundColor: this.pieColors,
						data: this.pieData,
						value_unit: this.showDataShare ? "%" : valueUnitsofProps[0],
					},
				],
			};
		},
		valuesSum() {
			// Round to 2 decimal places
			return (
				Math.round(
					this.pieDataProps
						.map((ap) => {
							return this.getApValue(ap, this.apValueColumn);
						})
						.reduce((partialSum, a) => partialSum + a, 0) * 100,
				) / 100
			);
		},
		comparisonValuesSum() {
			if (
				!["current_value", "gross_value", "base_value"].includes(
					// @ts-expect-error
					this.showValueComparisonWith,
				)
			) {
				console.error(
					"Don't know how to handle given argument for showValueComparisonWith:",
					this.showValueComparisonWith,
				);
			}
			return (
				Math.round(
					this.pieDataProps
						.map((ap) => {
							return this.getApValue(ap, this.showValueComparisonWith);
						})
						.reduce((partialSum, a) => partialSum + a, 0) * 100,
				) / 100
			); // Round to 2 decimal places
		},
		chartOptions() {
			return {
				cutout: `${this.chartCutoutPercentage}%`,
				aspectRatio: 1,
				responsive: true,
				maintainAspectRatio: true,
				plugins: {
					legend: {
						display: this.showDonutChartLabels,
						position: "top",
					},
				},
			};
		},
		totalLabel() {
			return I18n.t("global.total");
		},
		showValueComparison: function () {
			return this.showValueComparisonWith != null;
		},
	},
	mounted() {},
	methods: {
		getApValue(ap, valueColumn) {
			return valueColumn == "base_value"
				? ap.base_value
				: // @ts-expect-error
					ap.values[this.forPhaseId][valueColumn];
		},
		toggleValueColumn(event) {
			this.toggleButtonActive = !this.toggleButtonActive;
			// @ts-expect-error
			this.apValueColumn = this.toggleButtonActive
				? "current_value"
				: this.showValueComparisonWith || "gross_value";
		},
		toggleButtonOverlayText() {
			return I18n.t(
				`activerecord.values.phase.relevant_property_values_for_analysis.${this.apValueColumn == "current_value" ? "show_analysis_with_current_value" : "show_analysis_with_gross_value"}`,
			);
		},
	},
};
</script>
